/**
 * Keeps The Workday subscription tins together in the cart.
 *
 * A bundle line is any cart line with the property _bundle = "The Workday"
 * (added by sections/weekday-product.liquid in bundle subscription mode).
 *
 * 1. "Remove The Workday" (rendered by snippets/cart-products.liquid after the
 *    last bundle line) sets every bundle line to 0 in one /cart/update.js call.
 * 2. Safety net: on page load and after every cart lines update, the group must
 *    be exactly energy-strips + focus-strips + relax-strips at one shared
 *    quantity. If not, each remaining bundle line becomes a normal line on its
 *    "Subscribe & Save" plan (or one-time if it has none).
 *
 * Both refresh the cart by dispatching the same CartLinesUpdateEvent the theme
 * uses, so the drawer, cart page and header count update as usual.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const BUNDLE_NAME = 'The Workday';
const BUNDLE_HANDLES = ['energy-strips', 'focus-strips', 'relax-strips'];
const FALLBACK_PLAN_GROUP = 'Subscribe & Save';

const root = window.Shopify?.routes?.root || '/';
const urls = {
  cart: `${root}cart.js`,
  change: `${root}cart/change.js`,
  update: `${root}cart/update.js`,
  product: (handle) => `${root}products/${encodeURIComponent(handle)}.js`,
};

/** Events this script dispatched itself; the safety net ignores them. */
const ownEvents = new WeakSet();
let checking = false;

const isBundleLine = (item) => item?.properties?._bundle === BUNDLE_NAME;

/** Section ids of every cart view on the page, so responses carry fresh HTML. */
const sectionParams = () => {
  const ids = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  if (!ids.length) return {};
  return { sections: [...new Set(ids)].join(','), sections_url: window.location.pathname };
};

const getCart = async () => {
  const response = await fetch(urls.cart, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
};

const postJson = async (url, payload) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok || data.status || data.errors) {
    throw new Error(data.description || data.message || data.errors || `Cart request failed: ${response.status}`);
  }
  return data;
};

/**
 * Refreshes every cart view the theme's normal way. `cart` is a cart JSON
 * response (with `sections` when they were requested; without them each cart
 * view re-renders itself).
 */
const refreshCart = (target, action, lines, cart) => {
  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise });
  ownEvents.add(event);
  target.dispatchEvent(event);
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      sections: cart.sections,
      items: cart.items,
      itemCount: cart.item_count,
      source: 'wk-cart-bundle',
      didError: false,
    },
  });
};

const isValidGroup = (lines) => {
  if (lines.length !== BUNDLE_HANDLES.length) return false;
  const handles = lines.map((line) => line.handle);
  if (new Set(handles).size !== handles.length) return false;
  if (!BUNDLE_HANDLES.every((handle) => handles.includes(handle))) return false;
  return lines.every((line) => line.quantity === lines[0].quantity);
};

/** First plan of the product's "Subscribe & Save" group, or null for one-time. */
const fallbackPlanId = async (handle) => {
  const response = await fetch(urls.product(handle), { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Failed to fetch product ${handle}: ${response.status}`);
  const product = await response.json();
  const group = (product.selling_plan_groups || []).find((g) => g.name === FALLBACK_PLAN_GROUP);
  return group?.selling_plans?.[0]?.id ?? null;
};

const checkBundle = async () => {
  if (checking) return;
  checking = true;

  try {
    const cart = await getCart();
    const lines = cart.items.filter(isBundleLine);
    if (!lines.length || isValidGroup(lines)) return;

    // One call per line, one after another. Sections are only requested on the
    // last call, since only the final state needs rendering.
    let latest = null;
    const changed = [];
    for (const [index, line] of lines.entries()) {
      try {
        const sellingPlan = await fallbackPlanId(line.handle);
        const isLast = index === lines.length - 1;
        latest = await postJson(urls.change, {
          id: line.key,
          quantity: line.quantity,
          selling_plan: sellingPlan,
          properties: {},
          ...(isLast ? sectionParams() : {}),
        });
        changed.push({ id: line.key, quantity: line.quantity });
      } catch (error) {
        console.warn('[wk-cart-bundle] Could not convert bundle line', line.key, error);
        break;
      }
    }

    if (latest) refreshCart(document, 'update', changed, latest);
  } catch (error) {
    console.warn('[wk-cart-bundle] Bundle check failed', error);
  } finally {
    checking = false;
  }
};

// ---- "Remove The Workday" ----
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-wk-bundle-remove]') : null;
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  event.preventDefault();
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  try {
    const cart = await getCart();
    const keys = cart.items.filter(isBundleLine).map((item) => item.key);
    if (!keys.length) return;

    const updates = Object.fromEntries(keys.map((key) => [key, 0]));
    const result = await postJson(urls.update, { updates, ...sectionParams() });
    refreshCart(button, 'remove', keys.map((id) => ({ id, quantity: 0 })), result);
  } catch (error) {
    console.error('[wk-cart-bundle] Could not remove The Workday', error);
  } finally {
    if (button.isConnected) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
});

// ---- Safety net: page load, then after every cart lines update ----
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (ownEvents.has(event)) return;
  const settled = event.promise ? event.promise.then(() => {}, () => {}) : Promise.resolve();
  settled.then(checkBundle);
});

checkBundle();
